import { useIntl } from 'react-intl'

import {
  type OfferCheapestPriceV1,
  type OfferPriceV1,
  type OfferV11,
  type PimProductV11,
  type PimProductV1StockStatusEnum,
  type PrescriptionPrescriptionTypeEnum,
} from '@redteclab/api/clients/bully'

import { useExperimentsContext } from '../../../../experiments/components/ExperimentsContext'
import { experimentAccessorIsMarketplace } from '../../../../experiments/model/experimentAccessor'
import { useGlobalConfigContext } from '../../../../global-config'
import { offerAccessorIsOfferMirakl } from '../../../model/offerAccessor/offerAccessorIsOfferMirakl'
import { priceAccessorGetDisplayPrice } from '../../../model/priceAccessor/priceAccessorGetDisplayPrice'
import {
  type ErxRecommendedRetailPrice,
  priceAccessorGetErxRecommendedRetailPrice,
} from '../../../model/priceAccessor/priceAccessorGetErxRecommendedRetailPrice'
import { priceAccessorGetFormattedUnitPrice } from '../../../model/priceAccessor/priceAccessorGetFormattedUnitPrice'
import { priceAccessorGetThirtyDaysLowestPrice } from '../../../model/priceAccessor/priceAccessorGetThirtyDaysLowestPrice'
import {
  priceAccessorGetThirtyDaysPriceTrend,
  PRODUCT_THIRTY_DAYS_PRICE_TREND,
} from '../../../model/priceAccessor/priceAccessorGetThirtyDaysPriceTrend'
import { priceAccessorIsDiscountValid } from '../../../model/priceAccessor/priceAccessorIsDiscountValid'
import { productAccessorGetDiscountFromOffer } from '../../../model/productAccessor/productAccessorGetDiscountFromOffer'
import { productAccessorGetOfferByIdOrDefault } from '../../../model/productAccessor/productAccessorGetOfferByIdOrDefault'
import { productAccessorIsProductNotInCatalog } from '../../../model/productAccessor/productAccessorIsProductNotInCatalog'
import { productAccessorIsProductOutOfSale } from '../../../model/productAccessor/productAccessorIsProductOutOfSale'
import { productAccessorIsProductOutOfStock } from '../../../model/productAccessor/productAccessorIsProductOutOfStock'
import { useShowProductThirtyDaysPrice } from '../useShowProductThirtyDaysPrice'

export type ProductPrices = {
  discount: number
  displayPrice: OfferPriceV1 | null
  erxRecommendedRetailPrice: ErxRecommendedRetailPrice | null
  formattedUnitPrice: string | null
  isDiscountValid: boolean
  isOfferMirakl: boolean
  isProductNotInCatalog: boolean
  isProductOutOfSale: boolean
  isProductOutOfStock: boolean
  isThirtyDayPriceTrendDown: boolean
  showThirtyDaysPrice: boolean
  strikeThroughOfferPrice: OfferPriceV1 | null
  thirtyDayPriceTrend: PRODUCT_THIRTY_DAYS_PRICE_TREND
  thirtyDaysLowestPrice: OfferCheapestPriceV1 | null
}

/**
 * default empty prices
 */
const getDefaultEmptyPrices = (
  stock_status: PimProductV1StockStatusEnum | undefined,
  offer?: OfferV11,
): ProductPrices => ({
  discount: 0,
  displayPrice: null,
  erxRecommendedRetailPrice: null,
  formattedUnitPrice: null,
  isDiscountValid: false,
  /**
   * it could be that stock status not available but offer is mirakl, we still need to show seller box with correct seller
   */
  isOfferMirakl: offerAccessorIsOfferMirakl(offer?.offerType),
  isProductNotInCatalog: productAccessorIsProductNotInCatalog(stock_status),
  isProductOutOfSale: productAccessorIsProductOutOfSale(stock_status),
  isProductOutOfStock: productAccessorIsProductOutOfStock(stock_status),
  isThirtyDayPriceTrendDown: false,
  showThirtyDaysPrice: false,
  strikeThroughOfferPrice: null,
  thirtyDayPriceTrend: PRODUCT_THIRTY_DAYS_PRICE_TREND.PRICE_NO_CHANGE,
  thirtyDaysLowestPrice: null,
})

export const useProductPrices = ({
  currentPrescriptionType,
  offerId,
  product,
}: {
  currentPrescriptionType: PrescriptionPrescriptionTypeEnum
  offerId?: string | null
  product: PimProductV11
}): ProductPrices => {
  const { experiments } = useExperimentsContext()
  const { discountBoundaries, tenant } = useGlobalConfigContext()
  const isMarketplace = experimentAccessorIsMarketplace(experiments, tenant)
  const intl = useIntl()

  const offer = productAccessorGetOfferByIdOrDefault({
    isMarketplace,
    offerId,
    offers: product.offers,
  })

  const showThirtyDaysPrice = useShowProductThirtyDaysPrice(offer)

  const isProductOutOfStock = productAccessorIsProductOutOfStock(
    product.stock_status,
  )

  if (isProductOutOfStock || !offer) {
    return getDefaultEmptyPrices(product.stock_status, offer)
  }

  const discount = productAccessorGetDiscountFromOffer({
    currentPrescriptionType,
    offer,
    tenant,
  })

  /*
   * StrikeThroughOfferPrice currency should be the same as OfferPrice currency but for some reason on CH it's not
   * https://jira.shop-apotheke.com/browse/WSAWA-4040
   */
  if (
    offer.strikeThroughOfferPrice &&
    offer.offerPrice &&
    offer.strikeThroughOfferPrice.currency !== offer.offerPrice.currency
  ) {
    offer.strikeThroughOfferPrice.currency = offer.offerPrice.currency
  }

  const thirtyDayPriceTrend = priceAccessorGetThirtyDaysPriceTrend(
    offer,
    currentPrescriptionType,
  )

  return {
    discount,
    displayPrice: priceAccessorGetDisplayPrice(offer, currentPrescriptionType),
    erxRecommendedRetailPrice: priceAccessorGetErxRecommendedRetailPrice(
      offer,
      currentPrescriptionType,
    ),
    formattedUnitPrice: priceAccessorGetFormattedUnitPrice(offer, intl),
    isDiscountValid: priceAccessorIsDiscountValid(discount, discountBoundaries),
    isOfferMirakl: offerAccessorIsOfferMirakl(offer.offerType),
    isProductNotInCatalog: productAccessorIsProductNotInCatalog(
      product.stock_status,
    ),
    isProductOutOfSale: productAccessorIsProductOutOfSale(product.stock_status),
    isProductOutOfStock,
    isThirtyDayPriceTrendDown:
      thirtyDayPriceTrend === PRODUCT_THIRTY_DAYS_PRICE_TREND.PRICE_DOWN,
    showThirtyDaysPrice,
    strikeThroughOfferPrice: offer.strikeThroughOfferPrice ?? null,
    thirtyDayPriceTrend,
    thirtyDaysLowestPrice: priceAccessorGetThirtyDaysLowestPrice(offer),
  }
}
